import { useCallback, useEffect, useRef, useState } from "react"

import type { ColumnDef } from "../types/columns"
import { RUNS_VIEW_MODE, type RunsViewMode } from "../types/viewMode"

interface UseVisibleMetricColumnsParams {
  viewMode: RunsViewMode
  /** Opaque filter/sort/search context — any change resets default metric columns. */
  queryContext: unknown
  orderedMetricIds: string[]
  orderedSimMetricIds: string[]
  metricDisplayNames: Record<string, string>
  /** True once metric record metadata has finished loading. Initialization is deferred until this is true to avoid locking in empty defaults when rows arrive before metadata. */
  metricsMetadataReady: boolean
  baseRunColumns: ColumnDef[]
  baseSimColumns: ColumnDef[]
  tailColumns: ColumnDef[]
  defaultVisibleMetrics?: number
}

/**
 * Manages which columns are visible in the runs/simulations table.
 *
 * Default visible metric columns are chosen once per "session" (view mode or
 * query context change) from the first batch of rows, filtering out any metric
 * IDs that have no known display name. Subsequent infinite-scroll loads do not
 * reset the selection so user choices are preserved.
 */
export function useVisibleMetricColumns({
  viewMode,
  queryContext,
  orderedMetricIds,
  orderedSimMetricIds,
  metricDisplayNames,
  metricsMetadataReady,
  baseRunColumns,
  baseSimColumns,
  tailColumns,
  defaultVisibleMetrics = 5,
}: UseVisibleMetricColumnsParams): {
  visibleColumns: Set<string>
  setVisibleColumns: React.Dispatch<React.SetStateAction<Set<string>>>
  toggleColumn: (key: string) => void
} {
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set())

  // Refs so the viewMode-change effect can read latest values without TDZ deps
  const orderedMetricIdsRef = useRef<string[]>([])
  const orderedSimMetricIdsRef = useRef<string[]>([])
  const metricDisplayNamesRef = useRef<Record<string, string>>({})

  // Whether default metric columns have been initialized for each view mode
  const runsInitialized = useRef(false)
  const simsInitialized = useRef(false)

  const knownIds = useCallback(
    (ids: string[]) =>
      ids.filter((id) => metricDisplayNamesRef.current[id] !== undefined),
    [],
  )

  const defaultMetricKeys = useCallback(
    (ids: string[]) =>
      knownIds(ids)
        .slice(0, defaultVisibleMetrics)
        .map((id) => `metric:${id}`),
    [defaultVisibleMetrics, knownIds],
  )

  // Reset flags when query context (filters/sort/search) changes
  useEffect(() => {
    runsInitialized.current = false
    simsInitialized.current = false
  }, [queryContext])

  // Reset flags and re-initialize visible columns when view mode changes.
  // Uses refs because orderedMetricIds/metricDisplayNames are not yet in scope
  // at the time this effect first runs after a viewMode switch.
  useEffect(() => {
    runsInitialized.current = false
    simsInitialized.current = false

    if (viewMode === RUNS_VIEW_MODE.RUNS) {
      const nonMetricKeys = [...baseRunColumns, ...tailColumns].map(
        (col) => col.key,
      )
      setVisibleColumns(
        new Set([
          ...nonMetricKeys,
          ...defaultMetricKeys(orderedMetricIdsRef.current),
        ]),
      )
    } else {
      const nonMetricKeys = [...baseSimColumns, ...tailColumns].map(
        (col) => col.key,
      )
      setVisibleColumns(
        new Set([
          ...nonMetricKeys,
          ...defaultMetricKeys(orderedSimMetricIdsRef.current),
        ]),
      )
    }
  }, [viewMode, baseRunColumns, baseSimColumns, tailColumns, defaultMetricKeys])

  // Set default visible metric columns once rows and metadata are both ready
  useEffect(() => {
    metricDisplayNamesRef.current = metricDisplayNames
    orderedMetricIdsRef.current = orderedMetricIds
    if (
      viewMode !== RUNS_VIEW_MODE.RUNS ||
      orderedMetricIds.length === 0 ||
      !metricsMetadataReady
    ) {
      return
    }
    if (runsInitialized.current) {
      return
    }
    runsInitialized.current = true
    const nonMetricKeys = [...baseRunColumns, ...tailColumns].map(
      (col) => col.key,
    )
    setVisibleColumns(
      new Set([
        ...nonMetricKeys,
        ...orderedMetricIds
          .filter((id) => metricDisplayNames[id] !== undefined)
          .slice(0, defaultVisibleMetrics)
          .map((id) => `metric:${id}`),
      ]),
    )
  }, [
    orderedMetricIds,
    metricDisplayNames,
    metricsMetadataReady,
    viewMode,
    baseRunColumns,
    tailColumns,
    defaultVisibleMetrics,
  ])

  // Set default visible metric columns once rows and metadata are both ready
  useEffect(() => {
    orderedSimMetricIdsRef.current = orderedSimMetricIds
    if (
      viewMode !== RUNS_VIEW_MODE.SIMULATIONS ||
      orderedSimMetricIds.length === 0 ||
      !metricsMetadataReady
    ) {
      return
    }
    if (simsInitialized.current) {
      return
    }
    simsInitialized.current = true
    const nonMetricKeys = [...baseSimColumns, ...tailColumns].map(
      (col) => col.key,
    )
    setVisibleColumns(
      new Set([
        ...nonMetricKeys,
        ...orderedSimMetricIds
          .filter((id) => metricDisplayNames[id] !== undefined)
          .slice(0, defaultVisibleMetrics)
          .map((id) => `metric:${id}`),
      ]),
    )
  }, [
    orderedSimMetricIds,
    metricDisplayNames,
    metricsMetadataReady,
    viewMode,
    baseSimColumns,
    tailColumns,
    defaultVisibleMetrics,
  ])

  const toggleColumn = useCallback((key: string) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }, [])

  return { visibleColumns, setVisibleColumns, toggleColumn }
}
